import React, { useEffect, useState } from "react";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { IColumn, IFilter, IPagedListAction, IPagedViewResponse, RowFormatterFunction, SortOrder } from "../Declarations";
import { SelectColumnModal } from "../SelectColumnModal";
import { usePagedListData, usePagedListColumns, usePagedListFilters, usePagedListExport } from "../../../hooks";
import { PagedListHeader } from "./PagedListHeader";
import { PagedListTable } from "./PagedListTable";
import { PagedListPagination } from "./PagedListPagination";
import { separateActions, getRowClassName, buildPagesArray } from "../../../utils";
import "../PagedList.css";

type Props = {
    columns: IColumn[];
    headActions?: IPagedListAction[];
    itemActions?: IPagedListAction[];
    filters?: IFilter[];
    staticFilterValues?: Record<string, any>;
    defaultSortBy: string;
    defaultSortOrder?: SortOrder;
    pageSize?: number;
    getUrl: string;
    avatarUrl?: string;
    keyColumn?: string;
    rowFormatter: RowFormatterFunction;
    defaultRowClassName?: string;
    persistanceKey?: string;
    refreshNumber?: number;
    exportUrl?: string;
    exportIcon?: IconDefinition;
    items?: IPagedViewResponse;
    csvExportUrl?: string;
};

const PagedList: React.FC<Props> = ({
    columns,
    headActions = [] as IPagedListAction[],
    itemActions = [] as IPagedListAction[],
    filters = [] as IFilter[],
    staticFilterValues = {} as Record<string, any>,
    defaultSortBy,
    defaultSortOrder = SortOrder.ascending,
    pageSize = 25,
    getUrl,
    avatarUrl,
    keyColumn = "id",
    rowFormatter,
    defaultRowClassName = "bg-body",
    persistanceKey,
    refreshNumber,
    exportUrl,
    exportIcon,
    items,
    csvExportUrl
}) => {
    // Custom hooks for different concerns
    const dataHook = usePagedListData({
        getUrl,
        pageSize,
        defaultSortBy,
        defaultSortOrder,
        refreshNumber,
        items
    });

    const columnsHook = usePagedListColumns({
        columns,
        persistanceKey
    });

    const filtersHook = usePagedListFilters({
        filters,
        staticFilterValues,
        persistanceKey
    });

    const exportHook = usePagedListExport({
        exportUrl,
        csvExportUrl,
        pageSize,
        staticFilterValues,
        filterValues: filtersHook.filterValues,
        sortBy: dataHook.sortBy,
        sortOrder: dataHook.sortOrder
    });

    // Local state
    const [pages, setPages] = useState<number[]>([]);

    // Separate actions
    const { primaryActions, secondaryActions } = separateActions(itemActions);

    // Build pages when data changes
    useEffect(() => {
        if (dataHook.total && dataHook.total > 0) {
            const pagesArray = buildPagesArray(dataHook.offset, dataHook.total, pageSize);
            setPages(pagesArray);
        }
    }, [dataHook.total, dataHook.offset, pageSize]);

    // Fetch data when parameters change
    useEffect(() => {
        const requestValues = filtersHook.buildRequestValues(
            dataHook.offset,
            pageSize,
            dataHook.sortBy,
            dataHook.sortOrder
        );

        if (!filtersHook.lastRequestValues || 
            filtersHook.requestParamsChanged(filtersHook.lastRequestValues, requestValues)) {
            dataHook.getResults(requestValues);
            filtersHook.setLastRequestValues(requestValues);
        }
    }, [
        dataHook.offset,
        pageSize,
        dataHook.sortBy,
        dataHook.sortOrder,
        filtersHook.filterValues,
        filtersHook.lastRequestValues,
        filtersHook.requestParamsChanged,
        filtersHook.buildRequestValues,
        dataHook.getResults,
        filtersHook.setLastRequestValues
    ]);

    // Handle scroll
    const handleScroll = (value: number) => {
        // Scroll handling logic if needed
    };

    // Get row class name
    const getRowClassNameForItem = (item: any) => {
        return getRowClassName(item, defaultRowClassName, rowFormatter);
    };

    return (
        <div>
            <PagedListHeader
                filterValues={filtersHook.filterValues}
                onFilterValueChanged={filtersHook.handleFilterValueChanged}
                onFilterReset={filtersHook.handleFilterReset}
                total={dataHook.total || 0}
                first={dataHook.first || 0}
                last={dataHook.last || 0}
                headActions={headActions}
                exportUrl={exportUrl}
                csvExportUrl={csvExportUrl}
                exportIcon={exportIcon}
                onDownloadExport={exportHook.downloadExport}
                onExportCsv={exportHook.handleExportCsv}
            />

            <PagedListTable
                visibleColumns={columnsHook.visibleColumns}
                results={dataHook.results || []}
                hasLoadError={dataHook.hasLoadError}
                loadErrorMessage={dataHook.loadErrorMessage || null}
                hasResults={dataHook.hasResults}
                total={dataHook.total || 0}
                isBusy={dataHook.isBusy}
                tableWidth={columnsHook.tableWidth}
                sortBy={dataHook.sortBy}
                sortOrder={dataHook.sortOrder}
                primaryActions={primaryActions}
                secondaryActions={secondaryActions}
                keyColumn={keyColumn}
                avatarUrl={avatarUrl}
                onSortByClicked={dataHook.handleSortByClicked}
                onSelectColumnsClicked={columnsHook.handleSelectColumnsClicked}
                onScroll={handleScroll}
                getRowClassName={getRowClassNameForItem}
            />

            <PagedListPagination
                pages={pages}
                currentPage={dataHook.currentPage}
                lastPage={dataHook.lastPage}
                onPageChange={dataHook.setCurrentPage}
            />

            <SelectColumnModal
                columnOptions={columnsHook.columnOptions}
                show={columnsHook.showSelectColumnsModal}
                onSave={columnsHook.handleSelectColumnsUpdated}
                onReset={columnsHook.handleSelectColumnsReset}
                onClose={() => columnsHook.setShowSelectColumnsModal(false)}
            />
        </div>
    );
};

export { PagedList };
